import React from 'react';
import {
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';

import {
    initialOrderVolumeProfileComponentState,
    OrderVolumeProfileComponentState,
} from 'widgets/orderVolumeProfile/store/component/OrderVolumeProfileComponentState.ts';
import {
    initialOrderVolumeProfileDataState,
    OrderVolumeProfileDataState,
} from 'widgets/orderVolumeProfile/store/data/OrderVolumeProfileDataState.ts';
import {
    buildOrderVolumeProfileRequest,
    orderVolumeProfileRequestToStreamKey,
} from 'widgets/orderVolumeProfile/store/data/request-utils.ts';
import { selectPortfolioSelection } from 'portfolio/store/selectors.ts';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper.ts';
import {
    initialOrderVolumeProfileConfiguration,
    OrderVolumeProfileConfiguration,
} from 'widgets/orderVolumeProfile/store/configuration/OrderVolumeProfileConfiguration.ts';
import { buildOrderVolumeProfileData } from 'widgets/orderVolumeProfile/data/orderVolumeProfileData.ts';

export interface WidgetPanelOrderVolumeProfileProps
    extends WidgetPanelDetailProps {
    mode: 'data' | 'timeSeries' | 'buckets';
}

export default function WidgetPanelOrderVolumeProfile(
    props: WidgetPanelOrderVolumeProfileProps
) {
    const {
        widgetState,
        state,
        mode,
        widget: { id: widgetKey },
    } = props;

    const portfolioSelection = selectPortfolioSelection(state.state);
    const wrapper = new PortfolioSelectionWrapper(portfolioSelection);
    const componentState =
        (widgetState?.component as OrderVolumeProfileComponentState) ??
        initialOrderVolumeProfileComponentState;
    const config =
        (widgetState?.config as OrderVolumeProfileConfiguration) ??
        initialOrderVolumeProfileConfiguration;

    const request = wrapper.selectedOrder
        ? buildOrderVolumeProfileRequest(
              wrapper.selectedSymbol!,
              wrapper.symbology,
              wrapper.selectedOrder,
              config,
              componentState
          )
        : undefined;

    const streamKey = request
        ? orderVolumeProfileRequestToStreamKey(request)
        : undefined;

    const streamState: OrderVolumeProfileDataState = streamKey
        ? state.state.streams.data['orderVolumeProfile'][streamKey]?.data ??
          initialOrderVolumeProfileDataState
        : initialOrderVolumeProfileDataState;

    const data =
        streamState &&
        buildOrderVolumeProfileData(streamState, config.barSizeInMinutes ?? 1);

    for (const b of data.bucketedData) {
        showNiceDate(b, 'startTime');
        showNiceDate(b, 'endTime');
        showNiceDate(b, 'midTime');
    }

    switch (mode) {
        case 'timeSeries':
            return (
                <WidgetPanelGrid
                    key="timeSeries"
                    data={streamState?.timeSeries ?? []}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-timeSeries`}
                />
            );

        case 'buckets':
            return (
                <WidgetPanelGrid
                    key="buckets"
                    data={data?.bucketedData ?? []}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-bucketedData`}
                />
            );

        default:
            return (
                <div className="widget-panel">
                    <div className="title">OrderVolumeProfile Data</div>
                    <div className="field">
                        <span className={'label'}>Stream Key:</span>
                        {streamKey}
                    </div>
                    <JsonPanel data={data} />
                </div>
            );
    }
}

export function addOrderVolumeProfilePanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const renderData = () => (
        <WidgetPanelOrderVolumeProfile {...detailProps} mode={'data'} />
    );
    panes.push({
        menuItem: 'Data',
        render: renderData,
    });

    const renderBuckets = () => (
        <WidgetPanelOrderVolumeProfile {...detailProps} mode={'buckets'} />
    );
    panes.push({
        menuItem: 'Buckets',
        render: renderBuckets,
    });

    const renderTs = () => (
        <WidgetPanelOrderVolumeProfile {...detailProps} mode={'timeSeries'} />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderTs,
    });
}
